// Accounts in the app (server: crates/lanewerk-server/src/api.rs).
// - Who is signed in (GET /api/me). Without accounts (local use) the name and short name are chosen
//   per browser and kept in localStorage; with accounts they belong to the account.
// - The account menu behind the own avatar: name, short name, people admin, sign out.
// - The sign-in page (login links instead of passwords), the "no access" page and the welcome step
//   of the first admin.
import { loadMe, saveMe, shortOf, type Me } from "./presence";
import { PERSON_COLORS, css, personColor, setPersonColors } from "./palette";

export interface Account { id: string; name: string; short: string; email: string | null; admin: boolean; disabled: boolean; color?: number | null }
export interface Session { auth: boolean; user: Account | null }
export type Role = "read" | "write" | "admin";

export const ROLE_LABEL: Record<Role, string> = { read: "Lesen", write: "Bearbeiten", admin: "Verwalten" };

export class ApiError extends Error {
  constructor(readonly status: number, message: string) { super(message); }
}

/** JSON call to the server; changing calls carry the `X-Lanewerk` header the server requires (CSRF). */
export async function api<T>(path: string, method = "GET", body?: unknown): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (method !== "GET") headers["x-lanewerk"] = "1";
  if (body !== undefined) headers["content-type"] = "application/json";
  const r = await fetch(path, { method, headers, credentials: "same-origin", body: body === undefined ? undefined : JSON.stringify(body) });
  if (!r.ok) throw new ApiError(r.status, (await r.text().catch(() => "")) || `HTTP ${r.status}`);
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export async function loadSession(): Promise<Session> {
  try {
    const s = await api<Session>("./api/me");
    return { auth: !!s.auth, user: s.user ?? null };
  } catch {
    return { auth: false, user: null };
  }
}

/** Name and short name shown to the others (presence) and used as default owner in "Meine Aufgaben". */
export function identity(s: Session): Me {
  return s.user ? { name: s.user.name, short: s.user.short } : loadMe();
}

/** Login links point to the server's public URL, or are a path when the server does not know it. */
export const absolute = (link: string) => (link.startsWith("/") ? `${location.origin}${link}` : link);

const $ = <T extends HTMLElement>(sel: string) => document.querySelector(sel) as T;

// ---------- where to go after signing in ----------

const RETURN = "lanewerk.return";
/** Remembers the page someone wanted before the sign-in page (the login link always lands on `/`). */
export function rememberReturn() {
  const p = new URLSearchParams(location.search);
  if (!p.has("board") && !p.has("view")) return;
  try { localStorage.setItem(RETURN, JSON.stringify({ url: location.pathname + location.search, at: Date.now() })); } catch { /* fine */ }
}
export function takeReturn(): string | null {
  try {
    const raw = localStorage.getItem(RETURN);
    localStorage.removeItem(RETURN);
    const r = raw ? (JSON.parse(raw) as { url: string; at: number }) : null;
    // only same-site paths, only fresh (the link may be opened days later)
    if (r && r.url.startsWith("/") && !r.url.startsWith("//") && Date.now() - r.at < 7 * 86_400_000) return r.url;
  } catch { /* fine */ }
  return null;
}

// ---------- pages without a board ----------

function page(id: string) {
  document.body.classList.add("is-home", "is-signedout");
  $("#lw-boot")?.remove();
  for (const m of document.querySelectorAll<HTMLElement>("main")) m.hidden = m.id !== id;
}

export function showSignIn(invalidLink: boolean) {
  if (!invalidLink) rememberReturn();
  document.title = "Anmelden – Lanewerk";
  page("lw-signin");
  $("#lw-signin-error").hidden = !invalidLink;
}

/** The board was moved to the trash (while open, or an old link). */
export function showGone() {
  document.title = "Im Papierkorb – Lanewerk";
  page("lw-gone");
  document.body.classList.remove("is-signedout");
}

export function showDenied() {
  document.title = "Kein Zugriff – Lanewerk";
  page("lw-denied");
  document.body.classList.remove("is-signedout");
}

// ---------- account menu ----------

export class MeMenu {
  me: Me;
  private listeners = new Set<(me: Me) => void>();
  private btn = $<HTMLButtonElement>("#lw-me");
  private pop = $<HTMLFormElement>("#lw-me-pop");

  constructor(readonly session: Session) {
    this.me = identity(session);
    const user = session.user;
    $("#lw-me-hint").textContent = user
      ? `Angemeldet${user.email ? ` als ${user.email}` : ""}. Name und Kürzel gelten auf allen Boards.`
      : "Die anderen sehen deinen Namen am Mauszeiger. Gespeichert nur in diesem Browser.";
    $("#lw-me-account").hidden = !user;
    $("#lw-me-people").hidden = !user?.admin;
    $("#lw-nav-people").hidden = !user?.admin;
    this.btn.addEventListener("click", () => (this.pop.hidden ? this.open() : this.close()));
    this.pop.addEventListener("keydown", (e) => { e.stopPropagation(); if (e.key === "Escape") this.close(); });
    this.pop.addEventListener("submit", (e) => {
      e.preventDefault();
      void this.save($<HTMLInputElement>("#lw-me-name").value, $<HTMLInputElement>("#lw-me-short").value).then((ok) => { if (ok) this.close(); });
    });
    $("#lw-me-logout").addEventListener("click", () => void this.logout());
    // person colours (accounts only): load everyone's, offer my own
    $("#lw-me-colors-box").hidden = !user;
    if (user) {
      void this.loadColors();
      addEventListener("lanewerk:colors", () => this.render());
    }
    document.addEventListener("pointerdown", (e) => {
      if (!this.pop.hidden && !this.pop.contains(e.target as Node) && e.target !== this.btn) this.close();
    });
    this.render();
  }

  onChange(fn: (me: Me) => void) { this.listeners.add(fn); }

  /** Everyone's chosen colour (GET /api/people). */
  private async loadColors() {
    try { setPersonColors(await api<{ short: string; color?: number | null }[]>("./api/people")); } catch { /* keep the colours from the short names */ }
  }

  private renderColors() {
    const box = $("#lw-me-colors");
    const mine = this.session.user?.color ?? null;
    const pick = (label: string, value: number | null, rgb?: [number, number, number]) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = value === null ? "chip plain me-auto" : "swatch";
      if (value === null) b.textContent = label;
      else { b.style.background = css(rgb!); b.title = label; b.setAttribute("aria-label", label); }
      b.setAttribute("aria-pressed", String(mine === value));
      b.addEventListener("click", () => void this.setColor(value));
      return b;
    };
    box.replaceChildren(...PERSON_COLORS.map((c, i) => pick(c.name, i, c.rgb)), pick("Automatisch", null));
  }

  private async setColor(color: number | null) {
    try {
      const u = await api<Account>("./api/me", "PUT", { color: color ?? -1 });
      this.session.user = u;
      await this.loadColors();
      this.renderColors();
      // the swatches were replaced: keep the focus in the menu (Escape still closes it)
      document.querySelector<HTMLButtonElement>('#lw-me-colors [aria-pressed="true"]')?.focus();
      for (const l of this.listeners) l(this.me);
    } catch (e) {
      $("#lw-me-error").textContent = `Nicht gespeichert (${(e as Error).message}).`;
    }
  }

  /** Saves name and short name (account or browser). False when the server refused. */
  async save(name: string, short: string): Promise<boolean> {
    const err = $("#lw-me-error");
    err.textContent = "";
    const n = name.trim().slice(0, 60) || this.me.name;
    const s = (short.trim() || shortOf(n)).slice(0, 4).toUpperCase();
    if (this.session.user) {
      try {
        const u = await api<Account>("./api/me", "PUT", { name: n, short: s });
        this.session.user = u;
        this.me = { name: u.name, short: u.short };
      } catch (e) {
        err.textContent = `Nicht gespeichert (${(e as Error).message}).`;
        return false;
      }
    } else {
      this.me = { name: n, short: s };
      saveMe(this.me);
    }
    this.render();
    for (const l of this.listeners) l(this.me);
    return true;
  }

  private open() {
    $<HTMLInputElement>("#lw-me-name").value = this.me.name;
    $<HTMLInputElement>("#lw-me-short").value = this.me.short;
    $("#lw-me-error").textContent = "";
    if (this.session.user) this.renderColors();
    this.pop.hidden = false;
    this.btn.setAttribute("aria-expanded", "true");
    const r = this.btn.getBoundingClientRect();
    this.pop.style.transform = `translate(${Math.max(8, Math.min(r.right - 260, window.innerWidth - 268))}px, ${r.bottom + 8}px)`;
    $<HTMLInputElement>("#lw-me-name").focus();
  }

  close() {
    this.pop.hidden = true;
    this.btn.setAttribute("aria-expanded", "false");
  }

  private render() {
    this.btn.textContent = this.me.short;
    this.btn.style.background = css(personColor(this.me.short));
    this.btn.title = this.session.user ? `${this.me.name} – Konto` : `Du: ${this.me.name} – klicken zum Ändern`;
  }

  private async logout() {
    try { await api("./api/logout", "POST"); } catch { /* the cookie is cleared by the response; go anyway */ }
    location.href = "./";
  }

  /** First sign-in of the first admin (the setup link names the account "Admin"): ask for the name. */
  welcome(next: () => void) {
    const form = $<HTMLFormElement>("#lw-welcome");
    const name = $<HTMLInputElement>("#lw-welcome-name");
    const short = $<HTMLInputElement>("#lw-welcome-short");
    name.value = "";
    short.value = "";
    const overlay = $("#lw-welcome-overlay");
    name.addEventListener("input", () => { short.placeholder = shortOf(name.value) || "z. B. JUEN"; });
    form.addEventListener("keydown", (e) => e.stopPropagation());
    overlay.hidden = false;
    name.focus();
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      if (!name.value.trim()) return name.focus();
      void this.save(name.value, short.value).then((ok) => {
        if (!ok) { $("#lw-welcome-error").textContent = $("#lw-me-error").textContent; return; }
        $("#lw-welcome-error").textContent = "";
        overlay.hidden = true;
        next();
      });
    });
  }
}
